import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search as SearchIcon,
  FileText,
  PawPrint,
  User,
  Calendar,
  ShoppingBag,
  Users,
  ExternalLink,
  TrendingUp,
  ArrowRight,
  Shield,
  MessageSquare,
  Newspaper,
  HelpCircle,
  Briefcase,
  HeartHandshake,
  Building2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  post: { icon: FileText, color: 'bg-blue-500', label: 'Post' },
  pet: { icon: PawPrint, color: 'bg-orange-500', label: 'Pet' },
  user: { icon: User, color: 'bg-green-500', label: 'User' },
  event: { icon: Calendar, color: 'bg-purple-500', label: 'Event' },
  marketplace: { icon: ShoppingBag, color: 'bg-pink-500', label: 'Marketplace' },
  group: { icon: Users, color: 'bg-indigo-500', label: 'Group' },
  forum: { icon: MessageSquare, color: 'bg-cyan-500', label: 'Forum' },
  blog: { icon: Newspaper, color: 'bg-amber-500', label: 'Blog' },
  faq: { icon: HelpCircle, color: 'bg-teal-500', label: 'FAQ' },
  service: { icon: Briefcase, color: 'bg-rose-500', label: 'Service' },
  agency: { icon: HeartHandshake, color: 'bg-red-500', label: 'Rescue' },
  club: { icon: Building2, color: 'bg-emerald-500', label: 'Club' }
};

// Where the user lands when they click a Quick Search Category card with an
// empty search box. Browsing a section is more useful than "nothing happens."
// Types without a public listing page (pet, user, post) fall through to a
// toast hint asking the user to type a search term.
const TYPE_BROWSE_URL = {
  event: '/events',
  marketplace: '/marketplace',
  group: '/communities',
  forum: '/forums',
  blog: '/blog',
  faq: '/faq',
  service: '/services',
  agency: '/adoption-agencies',
  club: '/clubs',
};

export default function SearchPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [trending, setTrending] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [duckDuckGoUrl, setDuckDuckGoUrl] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch trending searches on mount (public-safe)
  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const res = await fetch(`${API}/api/search/trending`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setTrending(data.trending || []);
        }
      } catch (err) {
        console.error('Failed to fetch trending:', err);
      }
    };
    fetchTrending();
  }, []);

  // Perform search (public — works for guests too)
  const performSearch = useCallback(async (searchQuery, types = null) => {
    if (!searchQuery || searchQuery.length < 2) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const params = new URLSearchParams({ q: searchQuery, limit: '50' });
      if (types && types !== 'all') {
        params.append('types', types);
      }

      const res = await fetch(`${API}/api/search?${params}`, {
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        setResults(data.results || []);
        setCategories(data.categories || {});
        setDuckDuckGoUrl(data.duckduckgo_url || '');
        setSearchParams({ q: searchQuery });
      } else {
        toast.error('Search failed');
      }
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Failed to search');
    } finally {
      setLoading(false);
    }
  }, [setSearchParams]);

  // Get suggestions as user types
  const fetchSuggestions = async (q) => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const res = await fetch(`${API}/api/search/suggestions?q=${encodeURIComponent(q)}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (err) {
      console.error('Suggestions error:', err);
    }
  };

  // Handle search on initial load if query exists
  useEffect(() => {
    const initialQuery = searchParams.get('q');
    if (initialQuery) {
      setQuery(initialQuery);
      performSearch(initialQuery);
    }
  }, [searchParams, performSearch]);

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.length >= 2) {
      performSearch(query);
      setSuggestions([]);
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (query) {
      performSearch(query, tab === 'all' ? null : tab);
    }
  };

  // Filter results by tab
  const filteredResults = activeTab === 'all' 
    ? results 
    : results.filter(r => r.type === activeTab);

  // Result card component
  const ResultCard = ({ result }) => {
    const config = TYPE_CONFIG[result.type] || TYPE_CONFIG.post;
    const Icon = config.icon;
    
    return (
      <Card 
        className="hover:shadow-md transition-shadow cursor-pointer group"
        onClick={() => navigate(result.url)}
        data-testid={`search-result-${result.id}`}
      >
        <CardContent className="p-4">
          <div className="flex gap-4">
            {/* Image/Avatar */}
            <div className="flex-shrink-0">
              {result.image_url ? (
                <Avatar className="h-14 w-14 rounded-lg">
                  <AvatarImage src={result.image_url} alt={result.title} />
                  <AvatarFallback className="rounded-lg">
                    <Icon className="w-6 h-6 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className={`h-14 w-14 rounded-lg ${config.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium line-clamp-1 group-hover:text-primary transition-colors">
                    {result.title}
                  </h3>
                  {result.description && (
                    <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                      {result.description}
                    </p>
                  )}
                </div>
                <Badge variant="secondary" className="flex-shrink-0">
                  {config.label}
                </Badge>
              </div>
              
              {/* Metadata */}
              {result.metadata && (
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  {result.metadata.author && (
                    <span>by {result.metadata.author}</span>
                  )}
                  {result.metadata.price !== undefined && (
                    <span className="font-medium text-foreground">${result.metadata.price}</span>
                  )}
                  {result.metadata.likes !== undefined && (
                    <span>{result.metadata.likes} likes</span>
                  )}
                  {result.metadata.members !== undefined && (
                    <span>{result.metadata.members} members</span>
                  )}
                </div>
              )}
            </div>
            
            <ArrowRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity self-center" />
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!user) {
    // Public search is allowed — guests can still use the search page.
    // Previously blocked behind login; opened up for SEO + discovery.
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Search</h1>
        <p className="text-muted-foreground">Find posts, pets, people, events, and more</p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSubmit} className="relative mb-8" data-testid="search-form">
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <Input
            type="text"
            placeholder="Search for anything..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              fetchSuggestions(e.target.value);
            }}
            className="pl-12 pr-24 py-6 text-lg"
            data-testid="search-input"
          />
          <Button 
            type="submit" 
            className="absolute right-2 top-1/2 transform -translate-y-1/2"
            disabled={query.length < 2}
            data-testid="search-submit"
          >
            Search
          </Button>
        </div>

        {/* Suggestions Dropdown */}
        {suggestions.length > 0 && (
          <Card className="absolute z-10 w-full mt-1">
            <CardContent className="p-2">
              {suggestions.map((s, i) => (
                <button
                  key={s.text || i}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded hover:bg-muted flex items-center gap-2"
                  onClick={() => {
                    setQuery(s.text);
                    setSuggestions([]);
                    performSearch(s.text);
                  }}
                >
                  {TYPE_CONFIG[s.type]?.icon && (
                    <span className="text-muted-foreground">
                      {React.createElement(TYPE_CONFIG[s.type].icon, { className: 'w-4 h-4' })}
                    </span>
                  )}
                  {s.text}
                </button>
              ))}
            </CardContent>
          </Card>
        )}
      </form>

      {/* Results or Initial State */}
      {hasSearched ? (
        <>
          {/* Category Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
            <TabsList className="w-full justify-start overflow-x-auto">
              <TabsTrigger value="all" data-testid="tab-all">
                All ({results.length})
              </TabsTrigger>
              {Object.entries(categories).map(([type, count]) => {
                const config = TYPE_CONFIG[type];
                if (!config || count === 0) return null;
                return (
                  <TabsTrigger key={type} value={type} data-testid={`tab-${type}`}>
                    {config.label} ({count})
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          {/* Results */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : filteredResults.length > 0 ? (
            <div className="space-y-3">
              {filteredResults.map(result => (
                <ResultCard key={result.id} result={result} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <SearchIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No results found</h3>
              <p className="text-muted-foreground mb-4">
                Try different keywords or search the web
              </p>
            </div>
          )}

          {/* Web Search Option */}
          {duckDuckGoUrl && (
            <Card className="mt-8 bg-muted/50">
              <CardContent className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-muted-foreground">Privacy-focused web search</span>
                </div>
                <p className="mb-4">Can&apos;t find what you&apos;re looking for? Search the web with DuckDuckGo</p>
                <Button 
                  variant="outline" 
                  onClick={() => window.open(duckDuckGoUrl, '_blank')}
                  data-testid="duckduckgo-search"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Search &quot;{query}&quot; on DuckDuckGo
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        /* Initial State - Trending */
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold mb-4">
            <TrendingUp className="w-5 h-5 text-primary" />
            Trending Searches
          </h2>
          
          <div className="flex flex-wrap gap-2">
            {trending.map((t, i) => (
              <Button
                key={t}
                variant="secondary"
                size="sm"
                onClick={() => {
                  setQuery(t.term);
                  performSearch(t.term);
                }}
                data-testid={`trending-${i}`}
              >
                {t.term}
                {t.count && <Badge variant="outline" className="ml-2">{t.count}</Badge>}
              </Button>
            ))}
            
            {trending.length === 0 && (
              <div className="text-muted-foreground text-sm">
                Start typing to search the community
              </div>
            )}
          </div>

          {/* Quick Search Categories */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(TYPE_CONFIG).map(([type, config]) => (
              <Card 
                key={type}
                className="cursor-pointer hover:shadow-md transition-shadow"
                data-testid={`quick-cat-${type}`}
                onClick={() => {
                  if (query) {
                    // User typed something — run a filtered search.
                    setActiveTab(type);
                    performSearch(query, type);
                  } else {
                    // No query yet — browsing a section is more useful than
                    // silently no-op-ing. Falls back to the search page with
                    // the type preselected if no dedicated listing exists.
                    const dest = TYPE_BROWSE_URL[type];
                    if (dest) {
                      navigate(dest);
                    } else {
                      setActiveTab(type);
                      toast.info('Type a search term to find ' + config.label.toLowerCase() + 's');
                    }
                  }
                }}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <config.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">{config.label}s</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
